import {useConfig} from '@aboutyou/config';
import {useLegacyGrpcRequestCallback} from '@aboutyou/data-fetcher';
import {CheckoutV1_logoutLazy} from '@aboutyou/grpc-clients/grips/protos/checkout/checkout_v1.grpc.lazy';
import {useTranslation} from '@aboutyou/i18n';
import {Log, LogoutEvent} from '@aboutyou/mlm';
import {useTrackLogout} from '@aboutyou/tracking';
import {useShowToast} from '@aboutyou/ui-components';
import {useSessionId, useUser, useUserToken} from '@aboutyou/user';
import {doAsync} from '@aboutyou/utils';
import {useCallback} from 'react';
import {useHistory} from 'react-router';
import {useHelpCenterChatbot} from '../../../backend-services/HelpCenterChatbot/hooks/useHelpCenterChatbot';
import {useUpdateUserCacheOnLogout} from './useUpdateUserCacheOnLogout';

export const useLogoutHandler = () => {
  const {push} = useHistory();
  const {data: userData} = useUser();
  const updateUserCache = useUpdateUserCacheOnLogout();
  const showToast = useShowToast();
  const {t} = useTranslation();
  const trackLogout = useTrackLogout();
  const logoutRequestCallback = useLegacyGrpcRequestCallback(
    CheckoutV1_logoutLazy,
  );
  const {device} = useConfig();
  const apiToken = useUserToken() ?? '';
  const {updateCache: updateSessionCache} = useSessionId();
  const {logOut: logOutHelpCenterChatbot} = useHelpCenterChatbot();

  const logoutClickHandler = useCallback(
    (type: 'userAction' | 'sessionValidator' = 'userAction') => {
      if (!userData?.checkoutSession || !apiToken) {
        Log.warn(
          '@aboutyou/logic-components',
          `${LogoutEvent} - Missing checkoutSession or mobileApiToken`,
          {
            type,
          },
        );
      }

      if (userData) {
        logoutRequestCallback
          .mutateAsync({
            clearDesktopSession: device === 'desktop',
            customerSession: {
              authToken: apiToken,
            },
            checkoutSession: userData.checkoutSession ?? {
              secret: '',
              cookieValue: '',
            },
          })
          .then(() => {
            trackLogout('successful');
            Log.compare({
              actionName: LogoutEvent,
              success: true,
              attributes: {
                type,
                metadata: {},
              },
            });
            logOutHelpCenterChatbot();
          })
          .catch(error => {
            trackLogout('error');
            Log.compare({
              actionName: LogoutEvent,
              success: false,
              attributes: {
                type,
                metadata: {error},
              },
            });
          })
          .finally(() => {
            push('/');
            showToast({
              type: 'SUCCESS',
              content: t('You have been successfully logged out'),
            });
            updateUserCache();
            /**
             * We need to reset the session key here, otherwise, wishlist or basket will be fetched with the
             * old session key, which was used before the login. This will result in the wishlist or basket
             * potentially being populated
             */
            updateSessionCache(null);
            // allowing the session sharing with alternate locales to happens
            doAsync(() => {
              window.location.reload();
            });
          });
      }
    },
    [userData, userData?.checkoutSession, apiToken, updateSessionCache],
  );

  return {logout: logoutClickHandler};
};
